import type { TablePaginationConfig, TableProps } from "antd";
import type {
  ColumnsType,
  FilterValue,
  SorterResult,
} from "antd/es/table/interface";
import { Button } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import PaginatedTable from "@/components/UI/PaginatedTable";
import PageHeader from "@/components/UI/PageHeader";
import ClientDownloadButton from "@/components/UI/ClientDownloadButton/ClientDownloadButton";
import ReturnToDefaultButton from "@/components/UI/TableToolbar/ReturnToDefaultButton";
import { ColumnButton } from "@/components/UI/TableToolbar/ColumnsMenu/ColumnButton";
import {
  ColumnVisibility,
  type ColumnsType as ColumnStateType,
} from "@/types/tableState.types";
import { createPagination } from "@/utils/tableHelpers";

type SimpleTableTemplateProps<T extends object> = {
  title?: string;
  tooltipText?: string;
  fileName: string;
  data?: T[];
  downloadData?: object[];
  columns: ColumnsType<T>;
  loading?: boolean;
  setPagination?: (pagination: TablePaginationConfig) => void;
  pagination?: TablePaginationConfig;
  footer?: () => React.ReactNode;
  onRow?: (record: T, index?: number) => React.HTMLAttributes<HTMLElement>;
  rowKey?: TableProps<T>["rowKey"];
  onAddRecord?: () => void;
  addButtonLabel?: string;
  inertDownloadButton?: boolean;
  /**
   * Hide the workspace "Revert" control. Its confirm copy is "All Changes Will Be Lost",
   * which on a table of user-entered records reads as discarding those records rather than
   * resetting filters and sort. Opt in per screen; existing callers are unaffected.
   */
  hideReturnToDefault?: boolean;
  /**
   * Show the "Select and order columns" control from the advanced table toolbar.
   *
   * `ColumnButton` drives off a local column list rather than backend metadata, so a
   * static-data screen can offer the same form factor as a dataset-backed one. Column
   * titles come from the antd column defs, so nothing has to be declared twice.
   */
  enableColumnSelector?: boolean;
};

export default function SimpleTableTemplate<T extends object>({
  title,
  tooltipText,
  fileName,
  data,
  downloadData,
  columns,
  footer,
  onAddRecord,
  addButtonLabel = "Add New",
  inertDownloadButton = false,
  hideReturnToDefault = false,
  enableColumnSelector = false,
  pagination: externalPagination,
  setPagination: externalSetPagination,
  ...restTableProps
}: SimpleTableTemplateProps<T>) {
  const [localPagination, setLocalPagination] = useState<TablePaginationConfig>(
    () => createPagination(data?.length ?? 0),
  );
  const [activeFilters, setActiveFilters] = useState<
    Record<string, React.Key[] | null>
  >({});
  const [activeSorter, setActiveSorter] = useState<{
    columnKey?: React.Key;
    order?: "ascend" | "descend" | null;
  }>({});

  const resolvedPagination = externalPagination ?? localPagination;
  const resolvedSetPagination = externalSetPagination ?? setLocalPagination;
  const totalRecords = data?.length ?? 0;

  /** fieldName -> human title, taken from the antd column defs. */
  const columnTitles = useMemo(() => {
    const titles: Record<string, string> = {};
    columns.forEach((column) => {
      const dataIndex = "dataIndex" in column ? column.dataIndex : undefined;
      const key = String(
        column.key ??
          (Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex) ??
          "",
      );
      if (key)
        titles[key] = typeof column.title === "string" ? column.title : key;
    });
    return titles;
  }, [columns]);

  const defaultColumnState = useMemo<ColumnStateType[]>(
    () =>
      Object.keys(columnTitles).map((fieldName) => ({
        fieldName,
        visibility: ColumnVisibility.VISIBLE,
        mandatory: false,
      })),
    [columnTitles],
  );

  const [columnState, setColumnState] = useState<ColumnStateType[] | null>(
    null,
  );
  const [lockedColumns, setLockedColumns] = useState(0);
  const effectiveColumnState = columnState ?? defaultColumnState;

  const controlledColumns = useMemo(
    () =>
      columns.map((column) => {
        const dataIndex = "dataIndex" in column ? column.dataIndex : undefined;
        const columnKey = String(
          column.key ??
            (Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex) ??
            "",
        );

        return {
          ...column,
          filteredValue: activeFilters[columnKey] || null,
          sortOrder:
            activeSorter.columnKey === columnKey ? activeSorter.order : null,
        };
      }),
    [activeFilters, activeSorter, columns],
  );

  /** Apply the selector's visibility and ordering on top of the controlled columns. */
  const visibleColumns = useMemo(() => {
    if (!enableColumnSelector) return controlledColumns;

    const byKey = new Map(
      controlledColumns.map((column) => {
        const dataIndex = "dataIndex" in column ? column.dataIndex : undefined;
        const key = String(
          column.key ??
            (Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex) ??
            "",
        );
        return [key, column];
      }),
    );

    return effectiveColumnState
      .filter((entry) => entry.visibility === ColumnVisibility.VISIBLE)
      .map((entry) => byKey.get(entry.fieldName))
      .filter((column) => column !== undefined);
  }, [controlledColumns, effectiveColumnState, enableColumnSelector]);

  const { filteredCount, filteredDownloadData } = useMemo(() => {
    const hasActiveFilters = Object.values(activeFilters).some(
      (filterValues) => Array.isArray(filterValues) && filterValues.length > 0,
    );

    if (!hasActiveFilters)
      return { filteredCount: null, filteredDownloadData: null };

    const columnsByKey = Object.fromEntries(
      controlledColumns.map((column) => [column.key, column]),
    );

    const applyFilters = <R,>(rows: R[]) =>
      rows.filter((record) =>
        Object.entries(activeFilters).every(([key, filterValues]) => {
          if (!Array.isArray(filterValues) || filterValues.length === 0)
            return true;
          const column = columnsByKey[key];
          const onFilter = column?.onFilter;
          return filterValues.some((value) =>
            onFilter
              ? onFilter(value, record)
              : record[key as keyof R] === value,
          );
        }),
      );

    return {
      filteredCount: applyFilters(data || []).length,
      filteredDownloadData: applyFilters(downloadData ?? data ?? []),
    };
  }, [activeFilters, controlledColumns, data, downloadData]);

  const paginationTotal = filteredCount ?? totalRecords;

  useEffect(() => {
    const pageSize = resolvedPagination.pageSize ?? 25;
    const current = resolvedPagination.current ?? 1;
    const maxPage = Math.max(1, Math.ceil(paginationTotal / pageSize));
    const nextCurrent = Math.min(current, maxPage);

    if (
      resolvedPagination.total === paginationTotal &&
      resolvedPagination.current === nextCurrent
    ) {
      return;
    }

    resolvedSetPagination({
      ...resolvedPagination,
      current: nextCurrent,
      total: paginationTotal,
    });
  }, [resolvedPagination, resolvedSetPagination, paginationTotal]);

  const isDefault =
    Object.values(activeFilters).every(
      (filterValues) =>
        !Array.isArray(filterValues) || filterValues.length === 0,
    ) && !activeSorter.order;

  const resetTableState = useCallback(() => {
    setActiveFilters({});
    setActiveSorter({});
    resolvedSetPagination({ ...resolvedPagination, current: 1 });
  }, [resolvedPagination, resolvedSetPagination]);

  const handleTableChange = useCallback(
    (
      _: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
    ) => {
      setActiveFilters(filters as Record<string, React.Key[] | null>);

      if (Array.isArray(sorter)) {
        const firstSorter = sorter[0];
        setActiveSorter({
          columnKey: firstSorter?.columnKey,
          order: firstSorter?.order || null,
        });
        return;
      }

      setActiveSorter({
        columnKey: sorter?.columnKey,
        order: sorter?.order || null,
      });
    },
    [],
  );

  const renderSummaryFooter = useCallback(() => {
    if (footer) {
      return footer();
    }

    return (
      <div className="flex gap-4">
        <p className="font-bold">Total records: {data?.length || 0}</p>
        {filteredCount !== null && filteredCount !== (data?.length || 0) ? (
          <p className="font-bold">Selected records: {filteredCount}</p>
        ) : null}
      </div>
    );
  }, [data, filteredCount, footer]);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      {(title && tooltipText) || onAddRecord ? (
        <div className="flex justify-between items-center">
          {title && tooltipText ? (
            <div className="flex items-center">
              <PageHeader title={title} tooltipText={tooltipText} />
            </div>
          ) : null}
          {onAddRecord ? (
            <Button
              className="bg-[#06709B] text-white rounded-md"
              onClick={onAddRecord}
            >
              {addButtonLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        {enableColumnSelector ? (
          <ColumnButton
            columns={effectiveColumnState}
            setColumns={setColumnState}
            lockedColumns={lockedColumns}
            setLockedColumns={setLockedColumns}
            columnsTitleCreator={(column) => columnTitles[column] ?? column}
            initialColumns={defaultColumnState}
            initialLockedColumns={0}
          />
        ) : null}
        {hideReturnToDefault ? null : (
          <ReturnToDefaultButton
            reset={resetTableState}
            isDefault={isDefault}
          />
        )}
        <ClientDownloadButton
          data={filteredDownloadData ?? downloadData ?? data}
          fileName={fileName}
          inert={inertDownloadButton}
        />
      </div>
      <PaginatedTable
        data={data}
        columns={visibleColumns}
        footer={renderSummaryFooter}
        setPagination={resolvedSetPagination}
        pagination={resolvedPagination}
        onTableChange={handleTableChange}
        {...(restTableProps as any)}
      />
    </div>
  );
}
